import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Airline } from "@/lib/Types/DateListResponse/Airline";
import { UrlaubURLParams } from "@/lib/Types/URLParameters";
import fetchData from "@/lib/Fetchers/fetchData";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { AppConfig } from "@/lib/Types/AppConfig";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { getFilterKey } from "@/lib/Helpers/getFilterKey";
import { PRODUCT_SEARCH_OFFERS_QUERY } from "@/lib/Fetchers/GraphQlQueries/getProductSearchOffers";
import { channel } from "@/lib/Types/Channel";

interface ProductSearchOffersVariables {
  from: string;
  to?: string;
  mandant: string;
  profile: string;
  channel: string;
  cids?: string;
  country?: string;
  categories?: string;
  price?: string;
  stars?: number;
  duration?: string;
  roomType?: string;
  mealType?: string;
  departureAirports?: string;
  tourOperator?: string;
  transfer?: number;
  rentalCar?: number;
  railAndFly?: number;
  minRecom?: number;
  directFlight?: boolean;
  searchId?: string;
  subsource: string;
  room?: string;
  utmCampaign?: string;
  specialDealTypes?: string[];
  dateExact?: number;
  platformName: string;
  userId?: string;
  device?: string;
  app?: boolean;
  travellers?: string;
  groupBy: "airline";
}

interface ProductVariantAirline {
  iataCode?: string;
  name?: string;
}

/**
 * Shape returned by the productsearch variant query. The same endpoint yields
 * the available airlines AND the available stay/trip durations.
 */
export interface ProductVariants {
  airlines: Airline[];
  durationStay: number[];
  durationTrip: number[];
}

function parseDurations(raw: unknown): number[] {
  return ((raw as (number | null)[]) ?? [])
    .filter((n): n is number => typeof n === "number" && n > 0)
    .sort((a, b) => a - b);
}

/**
 * @param includeDuration When true the current search `duration` is sent, which
 * scopes the result to that stay length (correct for the airline filter). For
 * the duration tabs we need ALL available durations, so it must be false —
 * sending `duration` collapses `durationStay` to just that one value.
 */
async function fetchProductVariants(
  searchParams: UrlaubURLParams,
  appConfig: AppConfig,
  cids: string | undefined,
  includeDuration: boolean,
): Promise<ProductVariants> {
  const { envConfig, viewMode, userData, mpt } = appConfig;
  const isDurationZero = searchParams.duration === "0";

  // `priceCurveFrom` lives in HotelDetailURLParamsOnly (hotel detail only), while
  // this fn is typed with the shared UrlaubURLParams used by the liste airline
  // filter — read it via a loose cast. On liste it is never set, so `from` is
  // unchanged there; on hotel detail the duration tabs re-run groupBy from the
  // day picked in the price calendar (same start date as useDateListSearch).
  // `to` is kept unconditionally: groupBy sends no `duration`, so it needs a
  // window — dropping `to` would return no durations and hide the tabs.
  const priceCurveFrom = (searchParams as Record<string, string | undefined>)
    .priceCurveFrom;

  const variables: ProductSearchOffersVariables = {
    from: priceCurveFrom || searchParams.from,
    to: searchParams.to,
    mandant: viewMode.mandator.toString(),
    profile: viewMode.profile.toString(),
    channel: viewMode.channel,
    platformName: "ICARUS",
    subsource: searchParams.bf_subsource || "S17RRV10S17RR01",
    groupBy: "airline",

    ...(cids && { cids }),
    ...(searchParams.country && { country: searchParams.country }),
    ...(searchParams.categories && { categories: searchParams.categories }),
    ...(searchParams.stars && { stars: +searchParams.stars }),
    ...(searchParams.roomType && { roomType: searchParams.roomType }),
    ...(searchParams.mealType && { mealType: searchParams.mealType }),
    ...(searchParams.departureAirport && {
      departureAirports: searchParams.departureAirport,
    }),
    ...(searchParams.tourOperator && {
      tourOperator: searchParams.tourOperator,
    }),
    ...(searchParams.transfer && { transfer: +searchParams.transfer }),
    ...(searchParams.rentalCar && { rentalCar: +searchParams.rentalCar }),
    ...(searchParams.railAndFly && { railAndFly: +searchParams.railAndFly }),
    ...(searchParams.minRecommendation && {
      minRecom: +searchParams.minRecommendation,
    }),
    ...(searchParams.directFlight && {
      directFlight: !!searchParams.directFlight,
    }),
    ...(searchParams.searchId && { searchId: searchParams.searchId }),
    ...(searchParams.rooms && {
      room: searchParams.rooms,
      travellers: searchParams.rooms,
    }),
    ...(searchParams.utm_campaign && {
      utmCampaign: searchParams.utm_campaign,
    }),
    ...(searchParams.toVoucher && { specialDealTypes: ["TO_VOUCHER"] }),
    ...(userData?.userUid && { userId: userData.userUid }),
    ...(userData?.deviceType && { device: userData.deviceType }),
    ...(searchParams.app === "true" && { app: true }),
  };

  if (searchParams.price) {
    variables.price = `0_${parseInt(searchParams.price).toFixed(0)}`;
  }

  if (includeDuration) {
    if (isDurationZero) {
      variables.dateExact = 1;
    } else if (searchParams.duration) {
      variables.duration = searchParams.duration;
    }
  }

  const url = `${envConfig.graphqlApi}/comvel-productsearch-service/graphql`;

  sendKibanaLog({
    envConfig,
    transactionId: searchParams.transactionId,
    message: "AIRLINES START",
    component: "airlines",
    jsonMessage: variables,
    searchId: searchParams.searchId,
    category: "debug",
  });

  const data = await fetchData({
    url,
    method: "POST",
    params: {
      query: PRODUCT_SEARCH_OFFERS_QUERY,
      variables,
    },
    mpt: mpt || undefined,
    envConfig,
    service: "airlines",
    transactionId: searchParams.transactionId,
    searchId: searchParams.searchId,
    timeout: 60000,
  });

  if (data?.apiError) {
    sendKibanaLog({
      envConfig,
      transactionId: searchParams.transactionId,
      message: "AIRLINES FAIL",
      component: "airlines",
      jsonMessage: data.message,
      searchId: searchParams.searchId,
      category: "debug",
    });
    return { airlines: [], durationStay: [], durationTrip: [] };
  }

  const variantResponse =
    data?.data?.getProductSearchOffers?.comvelProductVariantResponse;

  const rawAirlines = (variantResponse?.airlines ??
    []) as ProductVariantAirline[];

  const airlines: Airline[] = rawAirlines
    .filter(
      (airline): airline is Required<ProductVariantAirline> =>
        !!airline?.iataCode && !!airline?.name,
    )
    .map(({ iataCode, name }) => ({ code: iataCode, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const durationStay = parseDurations(variantResponse?.durationStay);
  const durationTrip = parseDurations(variantResponse?.durationTrip);

  sendKibanaLog({
    envConfig,
    transactionId: searchParams.transactionId,
    message: "AIRLINES SUCCESS",
    component: "airlines",
    jsonMessage: { count: airlines.length, durations: durationStay.length },
    searchId: searchParams.searchId,
    category: "debug",
  });

  return { airlines, durationStay, durationTrip };
}

function useVariantContext(appConfig: AppConfig) {
  const { pathname } = appConfig;
  const searchParams = useReactiveSearchParams();

  const searchParamsObject = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params as UrlaubURLParams;
  }, [searchParams]);

  const cids = pathname?.match(/cid_(\d+)/)?.[1];

  return { searchParams, searchParamsObject, cids };
}

const useAirlines = (appConfig: AppConfig) => {
  const { envConfig, viewMode } = appConfig;
  const { searchParams, searchParamsObject, cids } =
    useVariantContext(appConfig);

  const filterKey = useMemo(
    () =>
      getFilterKey(searchParamsObject, [
        "sort",
        "searchId",
        "airline",
        "clickId",
        "preselectedOfferId",
      ]),
    [searchParamsObject],
  );

  const query = useQuery({
    queryKey: ["airlines", filterKey, viewMode?.channel, cids ?? null],
    queryFn: () => fetchProductVariants(searchParamsObject, appConfig, cids, true),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled:
      !!envConfig &&
      !!viewMode &&
      viewMode.channel === channel.PACKAGE &&
      searchParams.size !== 0,
  });

  return {
    data: query.data?.airlines ?? [],
    isLoading: query.isLoading,
  };
};

/**
 * Available stay-length durations (nights) for the current search, used by the
 * price-calendar duration tabs. Sent WITHOUT `duration` so the backend returns
 * every available stay length (not just the currently selected one). PACKAGE only.
 */
export const useDurations = (appConfig: AppConfig) => {
  const { envConfig, viewMode } = appConfig;
  const { searchParams, searchParamsObject, cids } =
    useVariantContext(appConfig);

  // `duration` is intentionally excluded: it is not sent and must not affect the
  // cache key, so switching the search duration never refetches the full list.
  // `priceCurveFrom` is intentionally KEPT: picking a day in the price calendar
  // must re-run groupBy to find the durations available for that day.
  const filterKey = useMemo(
    () =>
      getFilterKey(searchParamsObject, [
        "sort",
        "searchId",
        "airline",
        "clickId",
        "preselectedOfferId",
        "duration",
      ]),
    [searchParamsObject],
  );

  const query = useQuery({
    queryKey: ["durations", filterKey, viewMode?.channel, cids ?? null],
    queryFn: () =>
      fetchProductVariants(searchParamsObject, appConfig, cids, false),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled:
      !!envConfig &&
      !!viewMode &&
      viewMode.channel === channel.PACKAGE &&
      searchParams.size !== 0,
  });

  return {
    data: query.data?.durationStay ?? [],
    isLoading: query.isLoading,
  };
};

export default useAirlines;
